<template>
  <div class="table--wrrrapper">
    <div
      :class="`table ${loading ? 'loading' : ''}`"
      :style="`min-height: ${height}; max-height: ${height}`"
    >
      <div v-if="loading" class="loading">
        <Loader :loading="loading" />
      </div>
      <table>
        <thead>
          <tr>
            <th
              @click="`${item.sortable ? changeSort(item.column) : ''}`"
              v-for="(item, idx) in cols"
              :key="`col-${item.column}-${idx}`"
              :class="[item.column === 'action' ? 'no-print' : '']"
            >
              <div class="table-header">
                <span class="label" v-if="item.label !== 'id'">
                  {{ item.label }}
                </span>
                <span
                  v-if="item.sortable && sortCol === item.column"
                  class="sort-icon"
                  :class="[
                    sortOrder === 'DESC'
                      ? 'mdi mdi-chevron-down'
                      : 'mdi mdi-chevron-up'
                  ]"
                >
                </span>
              </div>
            </th>
          </tr>
        </thead>

        <tbody v-if="sortedRows && sortedRows.length">
          <tr v-for="(row, rIdx) in sortedRows" :key="rIdx">
            <!-- <template v-if="row && row.full_name !== 'ady_Uc'"> -->
            <td
              v-for="(col, idx) in cols"
              :key="`col-${row[col.column]}-${idx}`"
              :style="`width: ${col.width}px;`"
              :class="[col.column === 'action' ? 'no-print' : '']"
            >
              <slot
                v-if="col.column == 'action'"
                name="action"
                :actions="row"
              ></slot>
              <template v-else>
                <span v-if="col.formatAmount">
                  {{ formatAmount(row[col.column]) }}
                </span>
                <router-link
                  class="link"
                  v-else-if="col.link"
                  :to="{
                    name: col.routerName,
                    params: {
                      id: row.patient_id ? row.patient_id : String(row.id)
                    }
                  }"
                  >{{ row[col.column] }}</router-link
                >
                <a
                  class="link"
                  v-else-if="col.column == 'email'"
                  :href="`mailto:${row[col.column]}`"
                  >{{ row[col.column] }}</a
                >
                <span
                  :class="[
                    status.includes(col.column)
                      ? statusClasses(row[col.column])
                      : ''
                  ]"
                  v-else-if="col.column !== 'id'"
                  >{{
                    col.column == "serial"
                      ? rIdx + 1
                      : status.includes(col.column)
                      ? formatStatus(row[col.column], col)
                      : col.column == "date"
                      ? formatDate(row[col.column])
                      : col.column == "created_at"
                      ? formatCreatedAt(row[col.column])
                      : col.column == "updated_at"
                      ? formatCreatedAt(row[col.column])
                      : row[col.column]
                  }}</span
                >
              </template>
            </td>
            <!-- </template> -->
          </tr>
        </tbody>
        <tbody v-else-if="!loading">
          <tr>
            <td :colspan="cols.length" class="empty-state-cell">
              <div class="empty-state">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 80 80"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="40" cy="40" r="40" fill="var(--surface-2)" />
                  <path
                    d="M24 54V30C24 28.8954 24.8954 28 26 28H54C55.1046 28 56 28.8954 56 30V54"
                    stroke="var(--accent-500)"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <rect
                    x="28"
                    y="36"
                    width="24"
                    height="10"
                    rx="2"
                    fill="var(--accent-500)"
                    fill-opacity="0.15"
                  />
                  <rect
                    x="32"
                    y="40"
                    width="16"
                    height="2"
                    rx="1"
                    fill="var(--accent-500)"
                    fill-opacity="0.3"
                  />
                </svg>
                <div class="empty-state-title">No Data Available</div>
                <div class="empty-state-desc">
                  There is nothing to display here yet.
                </div>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot v-if="total">
          <tr>
            <td v-for="(item, idx) in cols" :key="`col-${item.column}-${idx}`">
              <span>
                {{
                  item.sum ? sum(item.column) : idx == 1 ? "Total" : ""
                }}</span
              >
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div class="footer">
      <paginate
        v-if="pagination"
        :total-pages="totalPages"
        :total="resultCount"
        :per-page="itemsPerPage"
        :current-page="currentPage"
        :pagechanged="onPageChanged"
      />
    </div>
  </div>
</template>

<script>
import Paginate from "@/components/Paginate";
import moment from "moment";
import Loader from "./Loader.vue";
import { formatedStatus, formatNumbers } from "../utils/helpers";

/**
 * DataTable Component with Sort Caching
 *
 * Features:
 * - Default sort column via defaultSortColumn prop
 * - Sort preference caching in localStorage
 * - Cache persists until user logs out
 * - Cache can be disabled via enableSortCaching prop
 *
 * Usage:
 * <DataTable
 *   :cols="columns"
 *   :rows="data"
 *   default-sort-column="created_at"
 *   :enable-sort-caching="true"
 * />
 */
export default {
  components: {
    Paginate,
    Loader
  },
  props: {
    rows: {
      required: true,
      type: Array
    },

    cols: {
      type: Array,
      required: true
    },

    options: {
      type: Object,
      required: false
    },

    caption: {
      required: false,
      type: String
    },
    loading: {
      type: Boolean,
      default: false
    },
    footer: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number
    },
    itemsPerPage: {
      type: Number
    },
    resultCount: {
      type: Number
    },
    onPageChanged: {
      type: Function,
      default: () => {}
    },
    totalPages: {
      type: Number,
      default: 0
    },
    pagination: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: "calc(100vh - 19.5rem)"
    },
    total: {
      type: Boolean,
      default: false
    },
    defaultSortColumn: {
      type: String,
      default: null
    },
    enableSortCaching: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      sortCol: null,
      sortOrder: "DESC",
      status: ["status", "historyStatus"]
    };
  },

  mounted() {
    // set sort
    this.setSortCol();
  },

  methods: {
    formatDate(date) {
      return moment(date).format("YYYY-DD-MM");
    },
    formatCreatedAt(date) {
      return moment(date).format("MMM D,YYYY hh:mm A");
    },
    formatStatus(status, col) {
      if (col.historyStatus) {
        if (status == 1) {
          return "Processed";
        } else if (status == 2) {
          return "In Queue";
        }
      } else return formatedStatus(status, col.userStatus, col.ledger);
    },
    formatAmount(val) {
      const amount = val ? "PKR " + formatNumbers(val) : "-";
      return amount;
    },
    setSortCol() {
      // Try to load cached sort preference first (only if caching is enabled)
      if (this.enableSortCaching) {
        const cachedSort = this.loadSortPreference();
        if (cachedSort && cachedSort.column) {
          const col = this.cols.find(col => col.column === cachedSort.column);
          if (col && col.sortable) {
            this.sortCol = cachedSort.column;
            this.sortOrder = cachedSort.order || "DESC";
            return;
          }
        }
      }

      // If defaultSortColumn is provided, use it
      if (this.defaultSortColumn) {
        const col = this.cols.find(
          col => col.column === this.defaultSortColumn
        );
        if (col && col.sortable) {
          this.sortCol = this.defaultSortColumn;
          return;
        }
      }

      // Fallback to original behavior - first sortable column
      const col = this.cols.filter(col => col.sort === true);
      if (col.length) {
        this.sortCol = col[0].column;
      }
    },

    saveSortPreference(column, order) {
      if (!this.enableSortCaching) return;

      try {
        const sortPreference = {
          column,
          order,
          timestamp: Date.now()
        };
        localStorage.setItem(
          "dataTableSortPreference",
          JSON.stringify(sortPreference)
        );
      } catch (error) {
        console.warn("Failed to save sort preference to localStorage:", error);
      }
    },

    loadSortPreference() {
      if (!this.enableSortCaching) return null;

      try {
        const cached = localStorage.getItem("dataTableSortPreference");
        if (cached) {
          const preference = JSON.parse(cached);
          // Check if preference is not too old (optional - you can remove this check)
          const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          if (Date.now() - preference.timestamp < oneDay) {
            return preference;
          }
        }
        return null;
      } catch (error) {
        console.warn(
          "Failed to load sort preference from localStorage:",
          error
        );
        return null;
      }
    },

    clearSortPreference() {
      try {
        localStorage.removeItem("dataTableSortPreference");
      } catch (error) {
        console.warn(
          "Failed to clear sort preference from localStorage:",
          error
        );
      }
    },

    resetToDefaultSort() {
      this.clearSortPreference();
      this.setSortCol();
    },

    sum(column) {
      let sum = 0;
      this.rows &&
        this.rows.map(row => {
          const value = Number(row[column]);
          sum += value;
        });
      return sum.toLocaleString();
    },

    changeSort(column) {
      if (this.sortCol === column) {
        this.sortOrder = this.sortOrder === "ASC" ? "DESC" : "ASC";
      } else {
        this.sortCol = column;
        this.sortOrder = "DESC";
      }

      // Save the new sort preference
      this.saveSortPreference(this.sortCol, this.sortOrder);
    },
    statusClasses(val) {
      return val == 1
        ? "active-column"
        : val == 2 || val == 5
        ? "pending"
        : val == 3
        ? "processed"
        : val == 4
        ? "cancel"
        : val == 7
        ? "cancel"
        : "-";
    }
  },
  computed: {
    sortedRows() {
      if (this.sortCol === null) {
        return this.rows;
      }
      const sortOrder = this.sortOrder === "ASC" ? 1 : -1;
      const sortCol = this.sortCol;
      return (
        this.rows &&
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.rows.sort((a, b) => {
          if (a[sortCol] < b[sortCol]) {
            return -1 * sortOrder;
            // a should come after b in the sorted order
          } else if (a[sortCol] > b[sortCol]) {
            return 1 * sortOrder;
            // and and b are the same
          } else {
            return 0;
          }
        })
      );
    }
  }
};
</script>

<style scoped>
.table--wrrrapper {
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: var(--shadow-sm);
  background: var(--surface);
  border: 1px solid var(--line);
  overflow: hidden;
}

.table > .loading {
  background: rgba(255, 255, 255, 0.62);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.table {
  position: relative;
  overflow: auto;
  z-index: 1;
  background: var(--surface);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--fs-sm) !important;
  font-variant-numeric: tabular-nums;
}

/* Responsive: horizontal scroll on small screens */
.table table {
  width: 100%;
  min-width: 900px;
  position: relative;
  border-collapse: separate;
  border-spacing: 0;
}

@media (max-width: 900px) {
  .table {
    overflow-x: auto;
  }

  .table table {
    min-width: 700px;
  }
}

/* Fixed Headers */
th {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--surface-2);
  font-weight: 700;
  font-size: var(--fs-sm);
  color: var(--muted);
  border-bottom: 1px solid var(--line);
  border-right: 1px solid var(--line);
  letter-spacing: 0;
  padding: 10px 12px;
  line-height: 1.35;
  transition: background var(--dur) var(--ease);
}

th[scope="row"] {
  position: -webkit-sticky;
  position: sticky;
  left: 0;
  z-index: 1;
}

th:not([scope="row"]) {
  left: 0;
  z-index: 3;
}

.table .table-header {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 18px;
  white-space: nowrap;
}

.table table thead tr th .label {
  font-weight: 700;
  font-size: var(--fs-sm);
  text-transform: none;
  letter-spacing: 0;
  color: var(--muted);
  line-height: 1.35;
  white-space: nowrap;
}

.table table tr th {
  cursor: pointer;
  background: var(--surface-2);
}

.table table tr td {
  padding: 10px 12px;
  font-size: var(--fs-sm);
  color: var(--text);
  font-weight: 500;
  line-height: 1.42;
  background: var(--surface);
  border-bottom: 1px solid var(--line);
  border-right: 1px solid var(--line);
  transition: background var(--dur) var(--ease);
  vertical-align: middle;
  overflow-wrap: anywhere;
}

.table table tr th:first-child,
.table table tr td:first-child {
  border-left: 1px solid var(--line);
}

.table table tr td:first-child {
  text-align: center;
  color: var(--muted);
  font-weight: 700;
}

/* Clean rows — no zebra striping; hairline separators carry the structure */

/* Row hover effect */
.table tbody tr:hover td:not(.empty-state-cell) {
  background: var(--accent-050) !important;
}

/* Action button styling */
.table .link,
.table .action-btn {
  font-weight: 700;
  font-size: var(--fs-sm);
  color: var(--accent-600);
  background: var(--accent-050);
  border-radius: var(--radius-sm);
  padding: 4px 14px;
  text-decoration: none;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
  border: none;
  cursor: pointer;
  display: inline-block;
}

.table .link:hover,
.table .action-btn:hover {
  background: var(--accent-600);
  color: var(--surface);
}

.active-column {
  color: var(--accent-600) !important;
  font-weight: 700;
}

.pending {
  color: var(--warning) !important;
  font-weight: 700;
}

.processed {
  color: var(--success) !important;
  font-weight: 700;
}

.cancel {
  color: var(--danger) !important;
  font-weight: 700;
}

/* Sort icons */
.mdi-chevron-down,
.mdi-chevron-up {
  color: var(--accent-600);
  font-size: 16px;
  line-height: 1;
  transition: color var(--dur) var(--ease);
}

.sort-icon {
  display: inline-flex;
  align-items: center;
}

/* Highlight active sort column */
th.sorted {
  background: var(--accent-050) !important;
  color: var(--accent-600) !important;
}

.table table tfoot,
.table table tfoot th,
.table table tfoot td {
  position: -webkit-sticky;
  position: sticky;
  bottom: 0;
  z-index: 4;
  background: var(--surface-2);
  font-weight: 700;
  color: var(--text);
  font-size: var(--fs-sm);
}

.table table tfoot tr td {
  background: var(--surface-2);
}

.table table tfoot {
  border-top: 1px solid var(--line-strong);
}

.table table tfoot tr td span {
  font-weight: 700;
}

.table--wrrrapper .footer {
  display: flex;
  justify-content: flex-end;
  margin: 10px 0;
}

@media print {
  .table {
    min-height: unset !important;
    max-height: unset !important;
    border-bottom: 1px solid #ebebeb;
  }

  .table tbody tr:last-child {
    border-bottom: 1px solid #ebebeb;
  }
}

.empty-state-cell {
  text-align: center;
  padding: 0;
  background: var(--surface) !important;
  border-bottom: 0 !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 260px;
  padding: 28px 20px;
  color: var(--muted);
}

.empty-state svg {
  width: 64px;
  height: 64px;
  margin-bottom: 4px;
}

.empty-state-title {
  margin: 0;
  font-size: var(--fs-body);
  font-weight: 700;
  line-height: 1.35;
  color: var(--ink-900);
}

.empty-state-desc {
  max-width: 280px;
  margin: 0;
  font-size: var(--fs-sm);
  font-weight: 500;
  line-height: 1.45;
  color: var(--muted);
}
</style>
